import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { NumberInput } from '@/shared/ui/inputs/NumberInput';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { normalizeDecimal } from '@/shared/utils/number';
import { apiLimitConfigPut } from '@/entities/limit-config';
import { apiPricingConfigPut } from '@/entities/pricing';
import type {
  ApiLimitConfigGetResponse,
  ApiLimitConfigPutBody,
} from '@/shared/types/api/limit-config';
import type {
  ApiPricingConfigResponse,
  ApiPricingConfigPutBody,
} from '@/shared/types/api/pricing';

type Props = {
  limit: ApiLimitConfigGetResponse;
  pricing: ApiPricingConfigResponse;
};

export const FeaturesPricingPricingForm = ({ limit, pricing }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const canEdit = usePerm('pricing.edit');

  const [limitValue, setLimitValue] = useState(String(limit.amount));
  const [splitFee, setSplitFee] = useState(pricing.split_fee_per_package);
  const [packagePricePerKg, setPackagePricePerKg] = useState(pricing.package_price_per_kg);
  const [photoFee, setPhotoFee] = useState(pricing.photo_report_fee);
  const [videoFee, setVideoFee] = useState(pricing.video_report_fee);
  const [proverkaFee, setProverkaFee] = useState(pricing.proverka_fee);
  const [returnFee, setReturnFee] = useState(pricing.return_fee);
  // Inputs may be emptied freely while typing; blank/invalid values are only
  // flagged (red border) after the section's Save was pressed, and each error
  // clears as soon as the field holds a valid number again.
  const [limitTried, setLimitTried] = useState(false);
  const [pricingTried, setPricingTried] = useState(false);
  const isInvalid = (v: string): boolean => {
    const trimmed = normalizeDecimal(v).trim();
    return trimmed === '' || !Number.isFinite(Number(trimmed));
  };
  const fieldError = (tried: boolean, v: string): string | undefined =>
    tried && isInvalid(v) ? t('common.required') : undefined;

  useEffect(() => {
    setLimitValue(String(limit.amount));
  }, [limit]);

  useEffect(() => {
    setSplitFee(pricing.split_fee_per_package);
    setPackagePricePerKg(pricing.package_price_per_kg);
    setPhotoFee(pricing.photo_report_fee);
    setVideoFee(pricing.video_report_fee);
    setProverkaFee(pricing.proverka_fee);
    setReturnFee(pricing.return_fee);
  }, [pricing]);

  const saveLimit = useMutate({
    api: (body: ApiLimitConfigPutBody) => apiLimitConfigPut(body),
    invalidateKeys: [queryKeys.limitConfig],
    onSuccess: () => toast.success(t('pricing.toast.savedLimit')),
    onError: (_e, m) => toast.error(m),
  });

  const savePricing = useMutate({
    api: (body: ApiPricingConfigPutBody) => apiPricingConfigPut(body),
    invalidateKeys: [queryKeys.pricing],
    onSuccess: () => toast.success(t('pricing.toast.savedPricing')),
    onError: (_e, m) => toast.error(m),
  });

  const onLimitChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setLimitValue(e.target.value);
  };

  const onSubmitLimit = (): void => {
    setLimitTried(true);
    if (isInvalid(limitValue)) return;
    saveLimit.mutate({ amount: Number(normalizeDecimal(limitValue)) });
  };

  const onSubmitPricing = (): void => {
    setPricingTried(true);
    if (
      [splitFee, packagePricePerKg, photoFee, videoFee, proverkaFee, returnFee].some(isInvalid)
    ) {
      return;
    }
    savePricing.mutate({
      split_fee_per_package: normalizeDecimal(splitFee),
      package_price_per_kg: normalizeDecimal(packagePricePerKg),
      photo_report_fee: normalizeDecimal(photoFee),
      video_report_fee: normalizeDecimal(videoFee),
      proverka_fee: normalizeDecimal(proverkaFee),
      return_fee: normalizeDecimal(returnFee),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title={t('pricing.limitSection')}>
        <NumberInput
          label={t('pricing.fields.limit')}
          prefix="$"
          value={limitValue}
          error={fieldError(limitTried, limitValue)}
          onChange={onLimitChange}
        />
        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              leftIcon={<Save className="h-4 w-4" />}
              loading={saveLimit.isPending}
              onClick={onSubmitLimit}
            >
              {t('pricing.save')}
            </Button>
          </div>
        )}
      </Section>

      <Section title={t('pricing.pricingSection')}>
        <NumberInput
          label={t('pricing.fields.splitFee')}
          prefix="$"
          value={splitFee}
          error={fieldError(pricingTried, splitFee)}
          onChange={(e) => setSplitFee(e.target.value)}
        />
        <NumberInput
          label={t('pricing.fields.packagePricePerKg')}
          prefix="$"
          value={packagePricePerKg}
          error={fieldError(pricingTried, packagePricePerKg)}
          onChange={(e) => setPackagePricePerKg(e.target.value)}
        />
        <NumberInput
          label={t('pricing.fields.photoFee')}
          prefix="$"
          value={photoFee}
          error={fieldError(pricingTried, photoFee)}
          onChange={(e) => setPhotoFee(e.target.value)}
        />
        <NumberInput
          label={t('pricing.fields.videoFee')}
          prefix="$"
          value={videoFee}
          error={fieldError(pricingTried, videoFee)}
          onChange={(e) => setVideoFee(e.target.value)}
        />
        <NumberInput
          label={t('pricing.fields.proverkaFee')}
          prefix="$"
          value={proverkaFee}
          error={fieldError(pricingTried, proverkaFee)}
          onChange={(e) => setProverkaFee(e.target.value)}
        />
        <NumberInput
          label={t('pricing.fields.returnFee')}
          prefix="$"
          value={returnFee}
          error={fieldError(pricingTried, returnFee)}
          onChange={(e) => setReturnFee(e.target.value)}
        />
        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              leftIcon={<Save className="h-4 w-4" />}
              loading={savePricing.isPending}
              onClick={onSubmitPricing}
            >
              {t('pricing.save')}
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
};
